import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { 
  StudyProgress, 
  QuizResult, 
  GameResult,
  GameAnswer,
  StudySession, 
  Achievement,
  User,
  UserPreferences 
} from '../types/database';
import { syncService } from '../services/sync/SyncService';
import { GameResultService } from '../services/game/GameResultService';
// import { sqliteDatabaseService } from '../services/database/SQLiteDatabaseService';

interface StudyState {
  // User data
  currentUser: User | null;
  userPreferences: UserPreferences;
  
  // Study progress
  studyProgress: StudyProgress[];
  currentLesson: string | null;
  currentSession: StudySession | null;
  
  // Quiz results
  quizResults: QuizResult[];
  currentQuiz: string | null;
  
  // Game results
  gameResults: GameResult[];
  currentGame: string | null;
  
  // Achievements
  achievements: Achievement[];
  
  // UI state
  isLoading: boolean;
  error: string | null;
  
  // Actions
  initializeUser: (user: User) => void;
  updatePreferences: (preferences: Partial<UserPreferences>) => void;
  
  // Study progress actions
  startLesson: (lessonId: string) => Promise<void>;
  updateLessonProgress: (lessonId: string, progress: number, timeSpent: number) => Promise<void>;
  completeLesson: (lessonId: string) => Promise<void>;
  getLessonProgress: (lessonId: string) => StudyProgress | null;
  
  // Quiz actions
  startQuiz: (quizId: string, lessonId: string) => Promise<void>;
  submitQuiz: (answers: any[], timeSpent: number) => Promise<QuizResult>;
  getQuizResults: (quizId?: string) => QuizResult[];
  
  // Game actions
  startGame: (gameId: string, lessonId: string) => Promise<void>;
  submitGame: (
    gameId: string,
    lessonId: string,
    classId: string,
    chapterId: string,
    score: number,
    maxScore: number,
    timeSpent: number,
    answers: GameAnswer[],
    won: boolean,
    roomsCompleted: number
  ) => Promise<GameResult>;
  getGameResults: (gameId?: string) => GameResult[];
  getGameAttempts: (gameId: string) => number;
  loadGameResultsFromServer: (userId: string) => Promise<void>;
  syncGameResultsToServer: () => Promise<{ synced: number; failed: number }>;
  
  // Session tracking
  startStudySession: (type: 'lesson' | 'quiz' | 'simulation' | 'practice' | 'game', metadata?: any) => Promise<void>;
  endStudySession: () => Promise<void>;
  
  // Achievement actions
  checkAchievements: () => Promise<Achievement[]>;
  
  // Sync actions
  syncData: () => Promise<void>;
  getSyncStatus: () => Promise<any>;
  
  // Utility actions
  clearData: () => Promise<void>;
  exportData: () => Promise<any>;
  importData: (data: any) => Promise<void>;
}

const defaultPreferences: UserPreferences = {
  theme: 'light',
  fontSize: 'medium',
  language: 'vi',
  notifications: true,
  autoSync: true,
  offlineMode: false
};

export const useStudyStore = create<StudyState>()(
  persist(
    (set, get) => ({
      // Initial state
      currentUser: null,
      userPreferences: defaultPreferences,
      studyProgress: [],
      currentLesson: null,
      currentSession: null,
      quizResults: [],
      currentQuiz: null,
      gameResults: [],
      currentGame: null,
      achievements: [],
      isLoading: false,
      error: null,

      // User actions
      initializeUser: (user: User) => {
        set({ currentUser: user });
        localStorage.setItem('userId', user.id);
      },

      updatePreferences: (preferences: Partial<UserPreferences>) => {
        set(state => ({
          userPreferences: { ...state.userPreferences, ...preferences }
        }));
      },

      // Study progress actions
      startLesson: async (lessonId: string) => {
        set({ isLoading: true, error: null });
        
        try {
          // Start study session
          await get().startStudySession('lesson', { lessonId });
          
          // Update current lesson
          set({ currentLesson: lessonId });
          
          // Initialize progress if not exists
          const existingProgress = get().getLessonProgress(lessonId);
          if (!existingProgress) {
            const newProgress: StudyProgress = {
              id: Date.now().toString(),
              userId: get().currentUser?.id || 'anonymous',
              lessonId,
              classId: extractClassId(lessonId),
              chapterId: extractChapterId(lessonId),
              status: 'in_progress',
              progress: 0,
              timeSpent: 0,
              lastAccessedAt: new Date(),
              bookmarks: []
            };
            
            set(state => ({
              studyProgress: [...state.studyProgress, newProgress]
            }));
            
            // Save to database
            // await sqliteDatabaseService.add('studyProgress', newProgress);
          }
          
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Unknown error' });
        } finally {
          set({ isLoading: false });
        }
      },

      updateLessonProgress: async (lessonId: string, progress: number, timeSpent: number) => {
        try {
          const currentProgress = get().getLessonProgress(lessonId);
          if (currentProgress) {
                         const updatedProgress: StudyProgress = {
               ...currentProgress,
               progress: Math.max(currentProgress.progress, progress),
               timeSpent: currentProgress.timeSpent + timeSpent,
               lastAccessedAt: new Date(),
               status: progress >= 100 ? 'completed' : 'in_progress',
               completedAt: progress >= 100 ? new Date() : undefined
             };
            
            set(state => ({
              studyProgress: state.studyProgress.map(p => 
                p.id === currentProgress.id ? updatedProgress : p
              )
            }));
            
            // Update database
            // await sqliteDatabaseService.update('studyProgress', updatedProgress);
            
            // ⚠️ DISABLED: Old sync method (Phase 3 auto-syncs via StudyHistoryService)
            // await syncService.trackLessonProgress(lessonId, progress, timeSpent);
          }
        } catch (error) {
          console.error('Failed to update lesson progress:', error);
        }
      },

      completeLesson: async (lessonId: string) => {
        await get().updateLessonProgress(lessonId, 100, 0);
        
        // Check for achievements
        const newAchievements = await get().checkAchievements();
        if (newAchievements.length > 0) {
          set(state => ({
            achievements: [...state.achievements, ...newAchievements]
          }));
        }
      },

      getLessonProgress: (lessonId: string) => {
        return get().studyProgress.find(p => p.lessonId === lessonId) || null;
      },

      // Quiz actions
      startQuiz: async (quizId: string, lessonId: string) => {
        set({ currentQuiz: quizId });
        await get().startStudySession('quiz', { quizId, lessonId });
      },

      submitQuiz: async (answers: any[], timeSpent: number) => {
        const { currentQuiz, currentUser } = get();
        if (!currentQuiz) throw new Error('No quiz in progress');
        
        // Calculate score (simplified)
        const score = Math.floor(Math.random() * 100) + 1; // Demo score
        const maxScore = 100;
        const percentage = (score / maxScore) * 100;
        const passed = percentage >= 70;
        
        const quizResult: QuizResult = {
          id: Date.now().toString(),
          userId: currentUser?.id || 'anonymous',
          quizId: currentQuiz,
          lessonId: '', // Extract from session
          score,
          maxScore,
          percentage,
          timeSpent,
          answers: answers.map((answer, index) => ({
            questionId: `question_${index}`,
            userAnswer: answer,
            correctAnswer: 'demo_answer', // Demo
            isCorrect: Math.random() > 0.5, // Demo
            timeSpent: Math.floor(timeSpent / answers.length)
          })),
          startedAt: new Date(),
          completedAt: new Date(),
          passed,
          attempts: 1
        };
        
        // Add to store
        set(state => ({
          quizResults: [...state.quizResults, quizResult],
          currentQuiz: null
        }));
        
        // Save to database
        // await sqliteDatabaseService.add('quizResults', quizResult);
        
        // End quiz session
        await get().endStudySession();
        
        return quizResult;
      },

      getQuizResults: (quizId?: string) => {
        const { quizResults } = get();
        if (quizId) {
          return quizResults.filter(r => r.quizId === quizId);
        }
        return quizResults;
      },

      // Game actions
      startGame: async (gameId: string, lessonId: string) => {
        set({ currentGame: gameId });
        await get().startStudySession('game', { gameId, lessonId });
      },

      submitGame: async (
        gameId: string,
        lessonId: string,
        classId: string,
        chapterId: string,
        score: number,
        maxScore: number,
        timeSpent: number,
        answers: GameAnswer[],
        won: boolean,
        roomsCompleted: number
      ) => {
        const { currentUser, gameResults } = get();
        
        // Calculate percentage
        const percentage = (score / maxScore) * 100;
        
        // Count previous attempts for this game
        const previousAttempts = gameResults.filter(r => r.gameId === gameId).length;
        
        const gameResult: GameResult = {
          id: `game_${Date.now()}`,
          userId: currentUser?.id || 'anonymous',
          gameId,
          lessonId,
          classId,
          chapterId,
          score,
          maxScore,
          percentage,
          timeSpent,
          answers,
          startedAt: new Date(Date.now() - timeSpent * 1000), // Tính ngược từ thời gian hiện tại
          completedAt: new Date(),
          won,
          attempts: previousAttempts + 1,
          roomsCompleted
        };
        
        // Add to store (local first)
        set(state => ({
          gameResults: [...state.gameResults, gameResult],
          currentGame: null
        }));
        
        // Save to WordPress backend (async, không block UI)
        try {
          await GameResultService.saveGameResult(gameResult);
          console.log('✅ Game result saved to WordPress');
        } catch (error) {
          console.error('⚠️ Failed to save to WordPress, kept in local only:', error);
          // Không throw error - vẫn giữ kết quả local
        }
        
        // End game session
        await get().endStudySession();
        
        console.log('🎮 Game result saved locally:', gameResult);
        
        return gameResult;
      },

      getGameResults: (gameId?: string) => {
        const { gameResults } = get();
        if (gameId) {
          return gameResults.filter(r => r.gameId === gameId);
        }
        return gameResults;
      },

      getGameAttempts: (gameId: string) => {
        const { gameResults } = get();
        return gameResults.filter(r => r.gameId === gameId).length;
      },

      loadGameResultsFromServer: async (userId: string) => {
        try {
          console.log('📥 Loading game results from WordPress...');
          const serverResults = await GameResultService.getGameResults(userId);
          
          // Merge với local results (không duplicate)
          set(state => {
            const localIds = new Set(state.gameResults.map(r => r.id));
            const newResults = serverResults.filter(r => !localIds.has(r.id));
            
            return {
              gameResults: [...state.gameResults, ...newResults]
            };
          });
          
          console.log(`✅ Loaded ${serverResults.length} game results from server`);
        } catch (error) {
          console.error('❌ Failed to load game results from server:', error);
        }
      },

      syncGameResultsToServer: async () => {
        const { gameResults } = get();
        
        console.log(`📤 Syncing ${gameResults.length} game results to WordPress...`);
        
        const result = await GameResultService.syncGameResults(gameResults);
        
        console.log(`✅ Sync complete: ${result.synced} synced, ${result.failed} failed`);
        
        return result;
      },

      // Session tracking
      startStudySession: async (type: 'lesson' | 'quiz' | 'simulation' | 'practice' | 'game', metadata?: any) => {
        const session: StudySession = {
          id: Date.now().toString(),
          userId: get().currentUser?.id || 'anonymous',
          type,
          startTime: new Date(),
          duration: 0,
          activities: [],
          metadata: metadata || {}
        };
        
        set({ currentSession: session });
        
        // Save to database
        // await sqliteDatabaseService.add('studySessions', session);
        
        // ⚠️ DISABLED: Old sync method (Phase 3 uses StudyHistoryService.startSession)
        // await syncService.startStudySession(session);
      },

      endStudySession: async () => {
        const { currentSession } = get();
        if (!currentSession) return;
        
        const endTime = new Date();
        const duration = Math.floor((endTime.getTime() - currentSession.startTime.getTime()) / 1000);
        
        // Update session
        const updatedSession = {
          ...currentSession,
          endTime,
          duration
        };
        
        set({ currentSession: null });
        
        // Update database
        // await sqliteDatabaseService.update('studySessions', updatedSession);
        
        // ⚠️ DISABLED: Old sync method (Phase 3 uses StudyHistoryService.endSession)
        // await syncService.endStudySession(currentSession.id, endTime, duration);
      },

      // Achievement actions
      checkAchievements: async () => {
        // ⚠️ DISABLED: Use AchievementService directly instead
        // const userId = get().currentUser?.id || 'anonymous';
        // return await syncService.checkAndUnlockAchievements(userId);
        return [];
      },

      // Sync actions
      syncData: async () => {
        set({ isLoading: true, error: null });
        
        try {
          // ⚠️ UPDATED: Use new syncQueue method
          await syncService.syncQueue();
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Unknown error' });
        } finally {
          set({ isLoading: false });
        }
      },

      getSyncStatus: async () => {
        // ⚠️ UPDATED: Return new sync status format
        return {
          isOnline: syncService.isOnline(),
          queueSize: syncService.getQueueSize(),
          isSyncing: syncService.isSyncInProgress(),
          lastSyncTime: syncService.getLastSyncTime()
        };
      },

      // Utility actions
      clearData: async () => {
        // await sqliteDatabaseService.clearDatabase();
        set({
          studyProgress: [],
          quizResults: [],
          achievements: [],
          currentLesson: null,
          currentSession: null,
          currentQuiz: null
        });
      },

      exportData: async () => {
        // return await sqliteDatabaseService.exportData();
        return {};
      },

      importData: async (data: any) => {
        // await sqliteDatabaseService.importData(data);
        
        // Refresh store data
        const [progress, quizResults, achievements] = await Promise.all([
          // sqliteDatabaseService.getAll<StudyProgress>('studyProgress'),
          // sqliteDatabaseService.getAll<QuizResult>('quizResults'),
          // sqliteDatabaseService.getAll<Achievement>('achievements')
          [], [], []
        ]);
        
        set({
          studyProgress: progress,
          quizResults,
          achievements
        });
      }
    }),
    {
      name: 'study-store',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        userPreferences: state.userPreferences,
        studyProgress: state.studyProgress,
        quizResults: state.quizResults,
        achievements: state.achievements
      })
    }
  )
);

// Utility functions
function extractClassId(lessonId: string): string {
  const match = lessonId.match(/lop-(\d+)/);
  return match ? `lop-${match[1]}` : 'unknown';
}

function extractChapterId(lessonId: string): string {
  const match = lessonId.match(/chuong-(\d+)/);
  return match ? `chuong-${match[1]}` : 'unknown';
}

// Initialize database when store is created - temporarily disabled
// sqliteDatabaseService.initialize().catch(console.error);
